import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  styled,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton as MuiIconButton,
} from "@mui/material";
import {
  LinkedIn,
  Email,
  WhatsApp,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import XIcon from "../../assets/icons/XIcon";
import {
  HeroSection,
  HeroTitle,
  HeroSubtitle,
} from "../../components/hero-section/HeroSection";
import useTeamMembers from "../../hooks/api-request/useTeamMembers";

const TeamCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.shape.borderRadius * 2,
  transition: "all 0.3s ease-in-out",
  position: "relative",
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: theme.shadows[8],
    "& .member-image": {
      transform: "scale(1.05)",
    },
    "& .social-overlay": {
      opacity: 1,
    },
  },
}));

const ImageWrapper = styled(Box)({
  position: "relative",
  overflow: "hidden",
  height: 360,
  borderRadius: "16px 16px 0 0",
});

const TeamMemberImage = styled(CardMedia)({
  height: "100%",
  transition: "transform 0.5s ease-in-out",
});

const SocialOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background:
    "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0) 100%)",
  display: "flex",
  alignItems: "flex-end",
  padding: theme.spacing(3),
  opacity: 0,
  transition: "opacity 0.3s ease-in-out",
}));

const SocialLinks = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  "& .MuiIconButton-root": {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      transform: "translateY(-2px)",
    },
    transition: "all 0.2s ease-in-out",
  },
}));

const ContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const Role = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: "0.875rem",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
}));

const BioText = styled(Typography)(({ theme }) => ({
  lineHeight: 1.6,
  cursor: "pointer",
  position: "relative",
  "&.truncated": {
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
  },
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(2),
    maxWidth: 600,
  },
}));

const CloseButton = styled(MuiIconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  top: theme.spacing(2),
  color: theme.palette.grey[500],
}));

const TeamMemberSkeleton = () => (
  <Grid item xs={12} sm={6} md={4}>
    <TeamCard elevation={0}>
      <Skeleton
        variant="rectangular"
        height={360}
        animation="wave"
        sx={{
          borderRadius: "16px 16px 0 0",
          backgroundColor: "rgba(0,0,0,0.06)",
        }}
      />
      <ContentWrapper>
        <Skeleton
          variant="text"
          width={100}
          height={24}
          animation="wave"
          sx={{ backgroundColor: "rgba(0,0,0,0.08)" }}
        />
        <Skeleton
          variant="text"
          width="70%"
          height={32}
          animation="wave"
          sx={{ backgroundColor: "rgba(0,0,0,0.08)" }}
        />
        <Box sx={{ mt: 1 }}>
          <Skeleton
            variant="text"
            animation="wave"
            height={20}
            sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
          />
          <Skeleton
            variant="text"
            animation="wave"
            height={20}
            width="80%"
            sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
          />
          <Skeleton
            variant="text"
            animation="wave"
            height={20}
            width="60%"
            sx={{ backgroundColor: "rgba(0,0,0,0.06)" }}
          />
        </Box>
      </ContentWrapper>
    </TeamCard>
  </Grid>
);

const TeamMember = ({ member }) => {
  const [bioOpen, setBioOpen] = useState(false);
  const { t } = useTranslation();

  const handleBioOpen = () => setBioOpen(true);
  const handleBioClose = () => setBioOpen(false);

  return (
    <>
      <Grid item xs={12} sm={6} md={4}>
        <TeamCard elevation={0}>
          <ImageWrapper>
            <TeamMemberImage
              className="member-image"
              image={member.image || "/default-profile.jpg"}
              title={member.name}
              sx={{
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
            <SocialOverlay className="social-overlay">
              <SocialLinks>
                {member.linkedin && (
                  <IconButton
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label="LinkedIn Profile"
                  >
                    <LinkedIn />
                  </IconButton>
                )}
                {member.twitter && (
                  <IconButton
                    href={member.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label="X Profile"
                  >
                    <XIcon />
                  </IconButton>
                )}
                {member.email && (
                  <IconButton
                    href={`mailto:${member.email}`}
                    size="small"
                    aria-label="Email Contact"
                  >
                    <Email />
                  </IconButton>
                )}
                {member.whatsapp && (
                  <IconButton
                    href={`https://wa.me/${member.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label="WhatsApp Contact"
                  >
                    <WhatsApp />
                  </IconButton>
                )}
              </SocialLinks>
            </SocialOverlay>
          </ImageWrapper>
          <ContentWrapper>
            <Role>{member.role_display}</Role>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "text.primary",
                mb: 1,
              }}
            >
              {member.name}
            </Typography>
            <BioText
              variant="body2"
              color="text.secondary"
              className="truncated"
              onClick={handleBioOpen}
              sx={{
                transition: "color 0.2s ease-in-out",
                "&::after": {
                  content: `"${t('team.readMore')}"`,
                  display: "block",
                  color: "primary.main",
                  fontWeight: 600,
                  marginTop: 1,
                  fontSize: "0.8rem",
                },
              }}
            >
              {member.bio}
            </BioText>
          </ContentWrapper>
        </TeamCard>
      </Grid>

      <StyledDialog
        open={bioOpen}
        onClose={handleBioClose}
        aria-labelledby="bio-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="bio-dialog-title" sx={{ pr: 6 }}>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {member.name}
          </Typography>
          <Typography variant="subtitle1" color="primary" sx={{ mt: 0.5 }}>
            {member.role_display}
          </Typography>
          <CloseButton aria-label="close" onClick={handleBioClose}>
            <CloseIcon />
          </CloseButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            {member.bio}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <SocialLinks>
            {member.linkedin && (
              <IconButton
                href={member.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="LinkedIn Profile"
              >
                <LinkedIn />
              </IconButton>
            )}
            {member.twitter && (
              <IconButton
                href={member.twitter}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="X Profile"
              >
                <XIcon />
              </IconButton>
            )}
            {member.email && (
              <IconButton
                href={`mailto:${member.email}`}
                size="small"
                aria-label="Email Contact"
              >
                <Email />
              </IconButton>
            )}
            {member.whatsapp && (
              <IconButton
                href={`https://wa.me/${member.whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                aria-label="WhatsApp Contact"
              >
                <WhatsApp />
              </IconButton>
            )}
          </SocialLinks>
          <Button
            onClick={handleBioClose}
            variant="contained"
            sx={{ ml: "auto" }}
          >
            {t('team.close')}
          </Button>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

const Team = () => {
  const { teamMembers, loading, error } = useTeamMembers();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';

  const renderContent = () => {
    if (loading || !teamMembers) {
      return [1, 2, 3, 4, 5, 6].map((i) => <TeamMemberSkeleton key={i} />);
    }

    if (error) {
      return (
        <Grid item xs={12}>
          <Alert
            severity="error"
            sx={{
              width: "100%",
              maxWidth: 600,
              mx: "auto",
              mt: 4,
            }}
          >
            {error.message || t('team.errors.loadFailed')}
          </Alert>
        </Grid>
      );
    }

    if (teamMembers.length === 0) {
      return (
        <Grid item xs={12}>
          <Alert
            severity="info"
            sx={{
              width: "100%",
              maxWidth: 600,
              mx: "auto",
              mt: 4,
            }}
          >
            {t('team.noMembers')}
          </Alert>
        </Grid>
      );
    }

    return teamMembers.map((member) => (
      <TeamMember key={member.id} member={member} />
    ));
  };

  return (
    <Box component="main">
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <HeroTitle>{t('team.title')}</HeroTitle>
              <HeroSubtitle>
                {t('team.subtitle')}
              </HeroSubtitle>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      <Box
        sx={{
          py: 8,
          backgroundColor: "background.default",
          minHeight: "60vh", // Ensures minimum height even when loading/empty
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={4}
            justifyContent="center"
            alignItems="stretch"
          >
            {renderContent()}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Team;
